// @flow
import { datadogRum } from '@datadog/browser-rum';
import { withStatusSink } from './requestStatusSink';

// Step names shared with fortress-backend (src/domain/constants/hudVoucherTracking.ts) so RUM actions,
// APM span tags and server logs join on the same value in the HUD monthly vouchers dashboard.
export type HudVoucherStep =
  | 'draft_started'
  | 'begin_date_updated'
  | 'setup_undone'
  | 'status_updated'
  | 'submitted_changes_undone'
  | 'execution_saved'
  | 'approval_saved'
  | 'approval_finalized'
  | 'marked_paid'
  | 'tenant_event_moved'
  | 'adjustment_amount_updated'
  | 'special_claim_created'
  | 'special_claim_updated'
  | 'special_claim_deleted'
  | 'misc_accounting_created'
  | 'misc_accounting_updated'
  | 'misc_accounting_deleted'
  | 'repayment_agreement_created'
  | 'repayment_agreement_updated'
  | 'repayment_agreement_amount_updated'
  | 'repayment_agreement_deleted'
  | 'regular_assistance_updated'
  | 'submitted_to_tracs'
  | 'tenant_events_submitted_to_tracs'
  | 'mat30_downloaded';

export type VoucherContext = {
  voucherId: string,
  status: ?string,
  contractNumber: ?string,
  propertyId: ?string,
};

type StepContext = { outcome: 'success' | 'failure', http_status?: number };

export type VoucherSnapshot = { id: ?string, status: ?string };

const CONTEXT_KEY = 'voucher';
const FALLBACK_STATUS = /Request failed \((\d{3})\)/;

// Tracking must never break a voucher action, so every SDK call is isolated.
const safely = (fn: () => void): void => {
  try {
    fn();
  } catch (e) {
    // swallow: observability is best-effort
  }
};

const mergeContext = (patch: Object): void => {
  const current = datadogRum.getGlobalContext()?.[CONTEXT_KEY] || {};
  datadogRum.setGlobalContextProperty(CONTEXT_KEY, { ...current, ...patch });
};

const currentVoucher = (): Object =>
  datadogRum.getGlobalContext()?.[CONTEXT_KEY] || {};

// The voucher on screen when a call starts. Read up front so a result that lands after the user has
// moved on is still attributed to the voucher the call was made for.
const snapshotVoucher = (): VoucherSnapshot => {
  try {
    const voucher = currentVoucher();
    return { id: voucher.id || null, status: voucher.status || null };
  } catch (e) {
    return { id: null, status: null };
  }
};

export const setVoucherContext = ({
  voucherId,
  status,
  contractNumber,
  propertyId,
}: VoucherContext): void =>
  safely(() => {
    // Only the same voucher keeps its tab and last step; a different voucher starts clean.
    const previous = currentVoucher();
    const base = previous.id === voucherId ? previous : {};
    datadogRum.setGlobalContextProperty(CONTEXT_KEY, {
      ...base,
      id: voucherId,
      status: status || null,
      contract_number: contractNumber || null,
      property_id: propertyId || null,
    });
  });

export const clearVoucherContext = (): void =>
  safely(() => datadogRum.removeGlobalContextProperty(CONTEXT_KEY));

export const trackVoucherTabViewed = (tab: string): void =>
  safely(() => {
    datadogRum.addAction('hud_voucher.tab_viewed', {
      voucher_step: 'tab_viewed',
      tab,
    });
    mergeContext({ tab });
  });

// With no `voucher` snapshot the step merges into whatever voucher is on screen. With one, the step is
// attributed to that voucher and merged only while it is still the voucher on screen.
export const trackVoucherStep = (
  step: HudVoucherStep,
  ctx: StepContext,
  voucher?: VoucherSnapshot,
): void =>
  safely(() => {
    const voucherAttrs = voucher?.id
      ? {
          voucher_id: voucher.id,
          ...(voucher.status ? { voucher_status: voucher.status } : {}),
        }
      : {};
    datadogRum.addAction(`hud_voucher.${step}`, {
      voucher_step: step,
      ...voucherAttrs,
      ...ctx,
    });
    const stillOnVoucher =
      !voucher || (!!voucher.id && currentVoucher().id === voucher.id);
    if (stillOnVoucher) {
      mergeContext({ last_step: step, last_outcome: ctx.outcome });
    }
  });

export const reportVoucherError = (
  error: Error,
  ctx: {
    step: HudVoucherStep,
    endpoint: string,
    http_status?: number,
    error_name?: string,
    voucher_id?: string,
  },
): void =>
  safely(() => {
    const { step, ...rest } = ctx;
    datadogRum.addError(error, { voucher_step: step, ...rest });
  });

export const getHttpStatus = (rejection: mixed): ?number => {
  if (
    rejection &&
    typeof rejection === 'object' &&
    typeof rejection.statusCode === 'number'
  ) {
    return rejection.statusCode;
  }
  if (typeof rejection === 'string') {
    const match = rejection.match(FALLBACK_STATUS);
    return match ? Number(match[1]) : undefined;
  }
  return undefined;
};

// Wraps one mutating voucher API call. `endpoint` is the route template (no ids), e.g.
// 'POST /hud-vouchers/{hudVoucherId}/approve'. The caller always gets the original result/rejection.
export function withVoucherTracking<T>(
  step: HudVoucherStep,
  endpoint: string,
  call: () => Promise<T>,
): Promise<T> {
  const voucher = snapshotVoucher();
  let httpStatus: ?number;
  let pending: Promise<T>;
  try {
    // The sink receives the status of the request this call starts, however late it resolves.
    pending = withStatusSink((status) => {
      httpStatus = status;
    }, call);
  } catch (e) {
    pending = Promise.reject(e);
  }
  return pending.then(
    (value) => {
      trackVoucherStep(step, { outcome: 'success' }, voucher);
      return value;
    },
    (rejection) => {
      // All tracking work sits in one guard so nothing in it can replace the original rejection.
      safely(() => {
        const status = httpStatus ?? getHttpStatus(rejection);
        const statusCtx = status ? { http_status: status } : {};
        trackVoucherStep(step, { outcome: 'failure', ...statusCtx }, voucher);
        // Server/rejection text can carry amounts and unit or household ids, so the reported
        // message is built from enums only.
        const error = new Error(
          `HUD voucher ${step} failed: ${endpoint} (${status ?? 'unknown'})`,
        );
        const errorName =
          rejection instanceof Error ? { error_name: rejection.name } : {};
        reportVoucherError(error, {
          step,
          endpoint,
          ...statusCtx,
          ...errorName,
          voucher_id: voucher.id || undefined,
        });
      });
      throw rejection;
    },
  );
}
